import { Injectable } from '@angular/core';
import { GlobalConfigService } from '../../features/config/global-config.service';
import { SyncConfig } from '../../features/config/global-config.model';
import { take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { addTask, updateTask } from '../../features/tasks/store/task.actions';
import { selectAllTasks } from '../../features/tasks/store/task.selectors';
import { Task } from '../../features/tasks/task.model';
import { ShowSubTasksMode } from '../../features/tasks/task.model';
import { WorkContextType } from '../../features/work-context/work-context.model';
import { Project } from '../../features/project/project.model';
import { WorklogExportSettingsCopy } from '../../features/worklog/worklog.model';
import { addProject } from '../../features/project/store/project.actions';
import { json } from 'stream/consumers';

@Injectable({
  providedIn: 'root',
})
export class planeSyncProviderService {
  private readonly n8n_BASE_issues =
    'https://n8n.labs.zenkod.de/webhook/b54d3f0a-7bd2-4a77-974f-4632bcde2235';
  private readonly n8n_BASE_projects =
    'https://n8n.labs.zenkod.de/webhook/b54d3f0a-7bd2-4a77-974f-4632bcde11234';
  constructor(
    private _globalConfigService: GlobalConfigService,
    private _store: Store,
  ) {}

  private jsonConfigParse(jsonConfig) {
    const parsedJsonConfig = JSON.parse(jsonConfig)
    return parsedJsonConfig
  }

  private async getPlaneIssues(jsonConfig): Promise<void> {
    try {
      const twoMonthsAgo = new Date();
      twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
      const formattedDate = twoMonthsAgo.toISOString();
      const parsedJsonConfig = this.jsonConfigParse(jsonConfig);
      parsedJsonConfig.forEach(async (jsonConfig) => {
        // You can use the properties of 'element' here as needed
  
        const response = await fetch(`${this.n8n_BASE_issues}`, {
          method: 'POST',
          headers: {
            'X-Hasura-Admin-Secret': jsonConfig.n8nToken,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            assignee_id: jsonConfig.userId,
            host: jsonConfig.host,
            states: ['Backlog', 'Todo', 'In Progress'],
            two_months_ago: formattedDate,
          }),
        });
  
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
  
        const data = await response.json();
        const issues = data.data[`${jsonConfig.host}_issues`];

        const allTasks = await this._store.select(selectAllTasks).pipe(take(1)).toPromise();
  
        issues.forEach((issue: any) => {
          const taskId = issue.id;
          const projectId = issue.project_id;
          const estimateValue = issue.estimate_point?.value ?? 0;
  
          const convertedEstimate = estimateValue * 3600000;
          const issueFullName = `${issue.issue_display_name} "${issue.name}"`;
          const taskExists = allTasks.some((task) => task.id === taskId);
  
          if (!taskExists) {
            const task: Task = {
              id: taskId,
              title: issueFullName,
              projectId: issue.workspace_id,
              created: new Date(issue.created_at).getTime(),
              timeSpent: 0,
              timeEstimate: convertedEstimate,
              isDone: false,
              doneOn: null,
              parentId: null,
              notes: issue.description_html || '',
              tagIds: [],
              attachments: [],
              subTaskIds: [],
              timeSpentOnDay: {},
              reminderId: null,
              repeatCfgId: null,
              plannedAt: null,
              _showSubTasksMode: ShowSubTasksMode.Show,
              issueId: null,
              issueProviderId: null,
              issueType: null,
              issueWasUpdated: null,
              issueLastUpdated: new Date(issue.UpdatedAt).getTime(),
              issueAttachmentNr: null,
              issueTimeTracked: null,
              issuePoints: null,
            };
  
            this._store.dispatch(
              addTask({
                task,
                workContextId: projectId,
                workContextType: WorkContextType.PROJECT,
                isAddToBacklog: true,
                isAddToBottom: false,
              }),
            );
          } else {
            const existingTask = allTasks.find((task) => task.id === taskId);
            if (existingTask) {
              const backendUpdatedTime = new Date(issue.UpdatedAt).getTime();
  
              // @ts-ignore
              if (backendUpdatedTime > existingTask.issueLastUpdated) {
                const convertedEstimate = issue.estimate_point
                  ? parseInt(issue.estimate_point, 10) * 60 * 60 * 1000
                  : undefined;
  
                this._store.dispatch(
                  updateTask({
                    task: {
                      id: taskId,
                      changes: {
                        title: issueFullName,
                        notes: issue.description_html || '',
                        timeEstimate: convertedEstimate,
                        issueLastUpdated: backendUpdatedTime,
                      },
                    },
                    isIgnoreShortSyntax: true,
                  }),
                );
              }
            }
          }
        });
      });
    } catch (error) {
      console.error(`Error processing issues from table:`, error);
    }
  }
  

  async syncPlaneData(): Promise<void> {
    this._globalConfigService.sync$
      .pipe(take(1))
      .subscribe(async (syncConfig: SyncConfig) => {
        const jsonConfig = syncConfig.jsonConfig

        try {
          await this.getPlaneProjects(jsonConfig);
          await this.getPlaneIssues(jsonConfig);
        } catch (error) {
          console.error(`Error syncing planeDB table:`, error);
        }
      });
  }

  private async getPlaneProjects(jsonConfig): Promise<void> {
    const parsedJsonConfig = this.jsonConfigParse(jsonConfig);
    
    await Promise.all(parsedJsonConfig.map(async (jsonConfig) => { // Use map and Promise.all for better handling of async operations
      try {
        const response = await fetch(`${this.n8n_BASE_projects}`, {
          method: 'POST',
          headers: {
            'X-Hasura-Admin-Secret': jsonConfig.n8nToken,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            assignee_id: jsonConfig.userId,
            host: jsonConfig.host
          }),
        });
  
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();

        const planeProjects = data.data[`${jsonConfig.host}_workspaces`];
        for (const planeProject of planeProjects) {
          const project = this.mapPlaneProjectToSuperProductivity(planeProject);
          this._store.dispatch(addProject({ project }));
        }
      } catch (error) {
        console.error(`Error fetching projects:`, error);
        throw error; // Optionally re-throw the error if you want it to propagate
      }
    }));
  }
  

  private mapPlaneProjectToSuperProductivity(planeProject: any): Project {
    const today = new Date().toISOString().split('T')[0];

    return {
      id: planeProject.id,
      title: planeProject.name,
      isArchived: false,
      isHiddenFromMenu: false,
      icon: 'Inbox',
      isEnableBacklog: false,
      taskIds: [],
      backlogTaskIds: [],
      noteIds: [],
      workStart: { [today]: 0 },
      workEnd: { [today]: 0 },
      breakTime: { [today]: 0 },
      breakNr: { [today]: 0 },
      advancedCfg: {
        worklogExportSettings: {} as Readonly<WorklogExportSettingsCopy>,
      },
      theme: {
        isAutoContrast: true,
        isDisableBackgroundGradient: false,
        primary: 'Inbox',
        huePrimary: '500',
        accent: '#ff4081',
        hueAccent: '500',
        warn: '#f44336',
        hueWarn: '500',
        backgroundImageDark: null,
        backgroundImageLight: null,
      },
    };
  }
}
